'use client';

import { useState, useEffect } from 'react';

let cachedResult: boolean | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 min

export function useAdmin(): boolean {
  const [isAdmin, setIsAdmin] = useState(() => {
    if (cachedResult !== null && Date.now() - cacheTimestamp < CACHE_TTL) {
      return cachedResult;
    }
    return false;
  });

  useEffect(() => {
    if (cachedResult !== null && Date.now() - cacheTimestamp < CACHE_TTL) {
      setIsAdmin(cachedResult);
      return;
    }

    let cancelled = false;

    fetch('/api/auth/check-admin')
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled) {
          const result = !!data.isAdmin;
          cachedResult = result;
          cacheTimestamp = Date.now();
          setIsAdmin(result);
        }
      })
      .catch(() => {
        if (!cancelled) setIsAdmin(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return isAdmin;
}

/** Same check as {@link useAdmin}, for non-React code (e.g. share exports). Shares its cache. */
export async function fetchIsAdmin(): Promise<boolean> {
  if (cachedResult !== null && Date.now() - cacheTimestamp < CACHE_TTL) return cachedResult;
  try {
    const data = await fetch('/api/auth/check-admin').then((r) => r.json());
    cachedResult = !!data.isAdmin;
    cacheTimestamp = Date.now();
    return cachedResult;
  } catch {
    return false;
  }
}

export function invalidateAdminCache() {
  cachedResult = null;
  cacheTimestamp = 0;
}
